/**
 * The signatures on a claim — "Verified by (stage 1)" and "Approved by
 * (stage 2, final)" — shown wherever the record is displayed, not only on
 * detail pages (B2's UI ruling).
 *
 * Stage 1 VERIFIES, stage 2 APPROVES, and only the approval may have side
 * effects. The two lines keep those meanings apart: `verified_by_name` is
 * written by a stage-1 advance (and cleared by rework — a cleared line is a
 * verification that no longer stands), `approver_name` only by the terminal
 * decision. A single-stage claim has no verifier, so its approval reads
 * "(final)" alone.
 *
 * Status casing differs by module (leaves are lowercase, expenses and tour
 * plans are capitalised), so it is normalised here rather than at each call
 * site — one place to change if a fourth module lands.
 */
const DECIDED = { approved: "Approved", rejected: "Rejected" };

export default function DecidedBy({ record, className = "" }) {
  const decision = DECIDED[String(record?.status ?? "").toLowerCase()];
  const name = record?.approver_name;
  const verifier = record?.verified_by_name;

  const lines = [];
  if (verifier) {
    lines.push(
      <p
        key="verified"
        className="text-[10px] leading-tight text-callisto-muted"
        data-testid={`verified-by-${record.id}`}
      >
        Verified by{" "}
        <span className="font-semibold text-callisto-navy">{verifier}</span>{" "}
        (stage 1)
      </p>
    );
  }
  // A decided record with no name is possible on rows predating approver
  // capture — render nothing rather than "Approved by undefined".
  if (decision && name) {
    const suffix =
      decision === "Approved" ? (verifier ? " (stage 2, final)" : " (final)") : "";
    lines.push(
      <p
        key="decided"
        className="text-[10px] leading-tight text-callisto-muted"
        data-testid={`decided-by-${record.id}`}
      >
        {decision} by{" "}
        <span className="font-semibold text-callisto-navy">{name}</span>
        {suffix}
      </p>
    );
  }

  if (!lines.length) return null;
  return <div className={className}>{lines}</div>;
}
